// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MITs
import { PageIds } from '@config';
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Component, HostListener, OnInit } from '@angular/core';
import {
  ActivatedRoute,
  ActivationEnd,
  NavigationEnd,
  Router,
} from '@angular/router';
import { environment } from '@environment';
import { easyclaHelp, pageSettings } from '@config';
import {
  AuthService,
  UserService,
  LfxAcsUiService,
  FoundationService,
  CompanyService,
  FontAwesomeDetectorService,
  FontAwesomeAutoFallbackService,
} from '@services';
import { Observable, of, forkJoin } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { generalConstants } from '@config';
import { datadogRum } from '@datadog/browser-rum';
import { Intercom } from '@supy-io/ngx-intercom';
import { NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';

declare global {
  interface Window {
    transcend?: {
      showConsentManager?: () => void;
    };
  }
}

@Component({
  selector: 'lfx-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  title = 'lfx';
  pageSettings;
  pageHasScroll;
  helpUrl: string;
  currentRoute = '';
  isSidebarCollapsed = true;
  mobileMode: boolean;
  mobileExpandedClicked = pageSettings.pageSidebarMinified;
  showSidebarToggleButton = false;

  isAdmin$ = of(false);
  intercomeBooted = false;
  window = window;

  constructor(
    public authService: AuthService,
    public userService: UserService,
    public router: Router,
    public intercom: Intercom,
    private route: ActivatedRoute,
    private LfxAcsUiService: LfxAcsUiService,
    private foundationService: FoundationService,
    public companyService: CompanyService,
    private tooltipConfig: NgbTooltipConfig,
    private fontAwesomeDetectorService: FontAwesomeDetectorService,
    private fontAwesomeAutoFallbackService: FontAwesomeAutoFallbackService
  ) {
    // Configure global tooltip settings
    this.tooltipConfig.tooltipClass = 'lf-tooltip';

    this.pageSettings = pageSettings;
    this.pageSettings.pageWithFooter = true;

    this.router.events.subscribe(event => {
      if (event instanceof ActivationEnd) {
        this.pageSettings.pageContentGreyBackground =
          event.snapshot.data.pageContentGreyBackground;
        this.pageSettings.pageContentCustomLayout =
          event.snapshot.data.pageContentCustomLayout || false;
      }

      if (event instanceof NavigationEnd) {
        this.currentRoute = this.router.url;
      }
    });

    this.setUserInLFxHeader();
    this.LfxAcsUiService.injectScripts();

    this.mobileMode = typeof window !== 'undefined' && window.innerWidth <= 767;
  }

  ngOnInit() {
    this.userService.initializeFavourite();
    const script = document.createElement('script');

    script.setAttribute('src', environment.lfxHeaderUrl);
    document.head.appendChild(script);
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        switchMap(() => this.getRouteHelpPath())
      )
      .subscribe(url => this.setHelpPath(url));
    this.initLFXHeaderLinks();

    this.isAdmin$ = this.userService.isCompanyAdminOrLFStaffUser();
  }

  // set page right collapse
  onToggleSidebarRight(val: boolean): void {
    if (this.pageSettings.pageSidebarTwo) {
      this.pageSettings.pageSidebarTwo = false;
    } else {
      this.pageSettings.pageSidebarTwo = true;
    }
  }

  // hide mobile sidebar
  onHideMobileSidebar(val: boolean): void {
    if (this.pageSettings.pageMobileSidebarToggled) {
      if (this.pageSettings.pageMobileSidebarFirstClicked) {
        this.pageSettings.pageMobileSidebarFirstClicked = false;
      } else {
        this.pageSettings.pageMobileSidebarToggled = false;
      }
    }
  }

  sidebarCollapsed(isSidebarCollapsed) {
    this.isSidebarCollapsed = isSidebarCollapsed;
  }

  // toggle mobile sidebar
  onToggleMobileSidebar(val: boolean): void {
    if (this.pageSettings.pageMobileSidebarToggled) {
      this.pageSettings.pageMobileSidebarToggled = false;
    } else {
      this.pageSettings.pageMobileSidebarToggled = true;
      this.pageSettings.pageMobileSidebarFirstClicked = true;
    }
  }

  // hide right mobile sidebar
  onHideMobileRightSidebar(val: boolean): void {
    if (this.pageSettings.pageMobileRightSidebarToggled) {
      if (this.pageSettings.pageMobileRightSidebarFirstClicked) {
        this.pageSettings.pageMobileRightSidebarFirstClicked = false;
      } else {
        this.pageSettings.pageMobileRightSidebarToggled = false;
      }
    }
  }

  // toggle right mobile sidebar
  onToggleMobileRightSidebar(val: boolean): void {
    if (this.pageSettings.pageMobileRightSidebarToggled) {
      this.pageSettings.pageMobileRightSidebarToggled = false;
    } else {
      this.pageSettings.pageMobileRightSidebarToggled = true;
      this.pageSettings.pageMobileRightSidebarFirstClicked = true;
    }
  }

  setUserInLFxHeader(): void {
    const lfHeaderElement: any = document.getElementById('lfx-header');

    if (!lfHeaderElement) {
      return;
    }

    lfHeaderElement.beforeLogout = () => {
      this.authService.clearSession();
    };

    if (this.authService.userProfile$) {
      this.authService.userProfile$.subscribe(data => {
        this.loadDataDogScript();

        if (data) {
          lfHeaderElement.authuser = data;
          const name =
            data.fullName ||
            (data.firstName?.trim()
              ? `${data.firstName} ${data.lastName || ''}`.trim()
              : '');
          const email =
            data.primaryEmail || data.emails?.[0]?.emailAddress || '';

          if (!name) {

            console.warn('No namefound for user', data);
          }

          if (!email) {

            console.warn('No email found for user', data);
          }

          forkJoin({
            isLFStaff: this.userService.isLFStaffUser().pipe(take(1)),
            company: this.companyService.getMyCompany().pipe(take(1)),
          }).subscribe(({ isLFStaff, company }) => {
            datadogRum.setUser({
              id: data['https://sso.linuxfoundation.org/claims/username'],
              name,
              email,
              isLFStaff,
              accountId: company?.id || '',
              accountName: company?.name || '',
            });
          });

          if (!this.intercomeBooted) {
            this.intercomeBooted = true;
            this.intercom.boot({
              api_base: 'https://api-iam.intercom.io',
              app_id: environment.intercomId,
              user_hash:
                data['https://sso.linuxfoundation.org/claims/intercom'] ||
                data['sub'],
              user_id: data['https://sso.linuxfoundation.org/claims/username'],
              name: data['name'],
              email: data['email'],
            });
          }
        }
      });
    }

    this.companyService.conglomerateOrgIdChanged.subscribe(orgId => {
      if (orgId) {
        this.showSelectOrgDropdown();
        const selectedOrg = this.companyService.selectedConglomerateOrg;
        if (selectedOrg) {
          this.updateDatadogOrgContext(selectedOrg.id, selectedOrg.name);
        }
      }
    });

    this.companyService.conglomerateOrgsLoaded.subscribe(isLoaded => {
      if (isLoaded && !this.companyService.showSelectOrgView) {
        this.showSelectOrgDropdown();
        // Update Datadog context when orgs are loaded
        const selectedOrg = this.companyService.selectedConglomerateOrg;
        if (selectedOrg) {
          this.updateDatadogOrgContext(selectedOrg.id, selectedOrg.name);
        }
      } else if (!isLoaded) {
        lfHeaderElement.searchobj = null;
      }
    });
  }

  showSelectOrgDropdown() {
    const lfHeaderElement: any = document.getElementById('lfx-header');

    const dropDownOrgs = this.companyService.conglomerateOrgs.map(org => ({
      name: org.name,
      id: org.slug,
      orgId: org.id,
      icon: org.allOrgsIcon,
      slug: org.slug,
    }));

    lfHeaderElement.searchobj = {
      items: dropDownOrgs,
      hideSearchIcon: true,
      title: 'Currently Viewing',
      position: 'right', // right | left,
      value: {
        id: this.companyService.selectedConglomerateOrg?.slug,
      },
      selectItem: selectedOrg => this.selectOrg(selectedOrg),
    };
  }

  selectOrg(org) {
    org.id = org.orgId;
    this.companyService.selectedConglomerateOrg = org;
    this.router.navigate([`${org.slug}/home`]);
    setTimeout(() => {
      this.companyService.setCurrentCompanyId(org.id);
      this.companyService.setCurrentCompanyB2BId(org.id);
      this.companyService.setCurrentCompanySlug(org.slug);
      this.companyService.conglomerateOrgIdChanged.next(org.id);
    }, 200);

    this.updateDatadogOrgContext(org.id, org.name);
  }

  getCurrentCompanySlug() {
    return '/' + this.companyService.getCurrentCompanySlug();
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll($event) {
    const doc = document.documentElement;
    const top = (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);

    if (top > 0) {
      this.pageHasScroll = true;
    } else {
      this.pageHasScroll = false;
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    // Update mobile mode property on resize
    if (typeof window !== 'undefined') {
      this.mobileMode = window.innerWidth <= 767;
    }
  }

  openCookieSettings(event: Event): void {
    event.preventDefault();
    window.transcend?.showConsentManager?.();
  }

  private setHelpPath(helpPath: string) {
    this.helpUrl = helpPath;
  }

  private getRouteHelpPath(): Observable<string> {
    let childRoute = this.route.root;

    while (childRoute.firstChild) {
      childRoute = childRoute.firstChild;
    }

    return childRoute.data.pipe(
      map(
        data =>
          easyclaHelp.easyClaHelpPaths[data.pageId] ||
          easyclaHelp.easyClaHelpPaths[PageIds.Faq]
      )
    );
  }

  private initLFXHeaderLinks() {
    const header: any = document.getElementById('lfx-header');

    if (header) {
      if (environment.docsUrl) {
        header.docslink = environment.docsUrl;
      }
      header.supportlink = generalConstants.header.supportLink;
    }
  }

  private loadDataDogScript() {
    if (!datadogRum.getInitConfiguration()) {
      datadogRum.init({
        applicationId: '84c11924-25b0-4977-a5de-c219eaee64fa',
        clientToken: 'pubae2606bdae56ae891089061d790ffda9',
        site: 'datadoghq.com',
        service: 'LFX Organization Dashboard',
        env: environment.production ? 'prod' : 'dev',
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        trackInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'allow',
      });

      datadogRum.startSessionReplayRecording();
    }
  }

  private updateDatadogOrgContext(accountId: string, accountName: string): void {
    if (datadogRum.getInitConfiguration()) {
      datadogRum.setUserProperty('accountId', accountId);
      datadogRum.setUserProperty('accountName', accountName);
    }
  }
}
